import { z } from 'zod';

/**
 * Client-facing training-quiz contracts. CORRECT ANSWERS NEVER APPEAR HERE —
 * the question bank + `correctIndex` live server-only in
 * `apps/api/src/lib/training-quiz-bank.ts`. The client receives shuffled
 * options (no answer key), echoes the `seed` on submit, and the server grades.
 */

const isoDateString = z.string().datetime();

/** One option as shown to the user. `index` is the DISPLAY position (0..3). */
export const quizOptionSchema = z.object({
  index: z.number().int().min(0).max(3),
  text: z.string(),
});
export type QuizOption = z.infer<typeof quizOptionSchema>;

export const quizQuestionPublicSchema = z.object({
  id: z.string(),
  prompt: z.string(),
  options: z.array(quizOptionSchema).length(4),
});
export type QuizQuestionPublic = z.infer<typeof quizQuestionPublicSchema>;

/** GET /metric-hub/training/quiz */
export const quizGetResponseSchema = z.object({
  /** False → the test is still locked (first three requirements unmet). */
  eligibleForTest: z.boolean(),
  /** Attempt seed; the client echoes it back on submit (single-use). */
  seed: z.string(),
  attempt: z.number().int().nonnegative(),
  passThreshold: z.number().int().min(1).max(5),
  /** 5 questions when eligible; empty when locked. */
  questions: z.array(quizQuestionPublicSchema).max(5),
});
export type QuizGetResponse = z.infer<typeof quizGetResponseSchema>;

/** POST /metric-hub/training/quiz/submit body. */
export const quizSubmitBodySchema = z.object({
  seed: z.string().min(1),
  answers: z
    .array(
      z.object({
        questionId: z.string(),
        selectedIndex: z.number().int().min(0).max(3),
      }),
    )
    .length(5),
});
export type QuizSubmitBody = z.infer<typeof quizSubmitBodySchema>;

/** POST submit response — per-question correctness only, never the answer key. */
export const quizSubmitResponseSchema = z.object({
  score: z.number().int().min(0).max(5),
  passed: z.boolean(),
  bestScore: z.number().int().min(0).max(5),
  attempts: z.number().int().nonnegative(),
  passedAt: isoDateString.nullable(),
  perQuestion: z.array(z.object({ questionId: z.string(), correct: z.boolean() })).length(5),
  /** Did this submission push the user over the line to certified. */
  certified: z.boolean(),
});
export type QuizSubmitResponse = z.infer<typeof quizSubmitResponseSchema>;

/** GET /metric-hub/training/certified-owners — emails (lowercased) with a cert. */
export const certifiedOwnersResponseSchema = z.object({
  items: z.array(z.string()),
  generatedAt: isoDateString,
});
export type CertifiedOwnersResponse = z.infer<typeof certifiedOwnersResponseSchema>;
